import React from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  TextProps,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { getLang, isRTL, LANGS, setLang, t } from './i18n';
import { brandGradient, buttonGradient, colors, font, radius } from './theme';

type Weight = 'regular' | 'semibold' | 'bold' | 'black';

/** Text in Cairo. Weight picks the font file (one family per weight). */
export function T({ w = 'regular', size = 14, color = colors.text, style, ...rest }: TextProps & { w?: Weight; size?: number; color?: string }) {
  // Direction and alignment follow the app language (a line that starts with digits, like
  // "2026/10/29 …", would otherwise be laid out by its first character and jump sides).
  const rtl = isRTL();
  return (
    <Text
      {...rest}
      style={[
        // Android (Fabric) aligns a text by its script inside the paragraph direction set by the root
        // view's `direction`: Arabic/Kurdish go right, Latin/numbers go to the paragraph's end. An
        // explicit `textAlign: 'right'` there means "opposite" and put titles on the LEFT, so the
        // explicit alignment is kept for the web app only (CSS needs it).
        { fontFamily: font[w], fontSize: size, color, lineHeight: Math.round(size * 1.6), writingDirection: rtl ? 'rtl' : 'ltr' },
        Platform.OS === 'web' ? { textAlign: rtl ? 'right' : 'left' } : null,
        style,
      ]}
    />
  );
}

export function Card({ children, style, onPress, accent }: { children: React.ReactNode; style?: StyleProp<ViewStyle>; onPress?: () => void; accent?: string }) {
  const body = <View style={[s.card, accent ? { borderColor: accent, borderWidth: 1.5 } : null, style]}>{children}</View>;
  if (!onPress) return body;
  return (
    <Pressable onPress={onPress} style={({ pressed }) => [{ opacity: pressed ? 0.85 : 1 }]}>
      {body}
    </Pressable>
  );
}

export function Pill({ label, bg, fg, style }: { label: string; bg: string; fg: string; style?: StyleProp<ViewStyle> }) {
  return (
    <View style={[s.pill, { backgroundColor: bg }, style]}>
      <T w="bold" size={11} color={fg}>{label}</T>
    </View>
  );
}

type ButtonKind = 'primary' | 'outline' | 'danger' | 'soft';

export function Button({ label, onPress, kind = 'primary', icon, disabled, loading, style }: {
  label: string; onPress: () => void; kind?: ButtonKind; icon?: keyof typeof Ionicons.glyphMap;
  disabled?: boolean; loading?: boolean; style?: StyleProp<ViewStyle>;
}) {
  const fg = kind === 'primary' ? colors.white : kind === 'danger' ? colors.danger : colors.violet;
  const content = (
    <View style={s.btnRow}>
      {loading ? <ActivityIndicator color={fg} /> : icon ? <Ionicons name={icon} size={18} color={fg} /> : null}
      <T w="bold" size={15} color={fg}>{label}</T>
    </View>
  );
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || loading}
      style={({ pressed }) => [{ opacity: disabled ? 0.45 : pressed ? 0.85 : 1 }, style]}
    >
      {kind === 'primary' ? (
        <LinearGradient colors={[...buttonGradient]} start={{ x: 1, y: 0 }} end={{ x: 0, y: 0 }} style={s.btn}>
          {content}
        </LinearGradient>
      ) : (
        <View
          style={[
            s.btn,
            kind === 'danger' && { backgroundColor: colors.white, borderWidth: 1.5, borderColor: '#F2C3C3' },
            kind === 'outline' && { backgroundColor: colors.white, borderWidth: 1.5, borderColor: colors.violet },
            kind === 'soft' && { backgroundColor: colors.violetTint },
          ]}
        >
          {content}
        </View>
      )}
    </Pressable>
  );
}

/** Small round action (call, map, copy). */
export function IconChip({ icon, label, onPress, tone = 'violet' }: { icon: keyof typeof Ionicons.glyphMap; label: string; onPress: () => void; tone?: 'violet' | 'turquoise' }) {
  const bg = tone === 'violet' ? colors.violetTint : colors.turquoiseTint;
  const fg = tone === 'violet' ? colors.violetDark : colors.turquoiseDark;
  return (
    <Pressable onPress={onPress} style={({ pressed }) => [s.chip, { backgroundColor: bg, opacity: pressed ? 0.7 : 1 }]}>
      <Ionicons name={icon} size={15} color={fg} />
      <T w="bold" size={12} color={fg}>{label}</T>
    </Pressable>
  );
}

/** Gradient header (logo colours) that sits under the status bar. */
export function Header({ children, style }: { children: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  const insets = useSafeAreaInsets();
  return (
    <LinearGradient colors={[...brandGradient]} start={{ x: 1, y: 0 }} end={{ x: 0, y: 1 }} style={[{ paddingTop: insets.top + 14 }, s.header, style]}>
      {children}
    </LinearGradient>
  );
}

export function Screen({ header, children, refreshing, onRefresh, footerSpace = 90 }: {
  header?: React.ReactNode; children: React.ReactNode; refreshing?: boolean; onRefresh?: () => void; footerSpace?: number;
}) {
  return (
    <View style={{ flex: 1, backgroundColor: colors.bg }}>
      <ScrollView
        contentContainerStyle={{ paddingBottom: footerSpace }}
        refreshControl={onRefresh ? <RefreshControl refreshing={!!refreshing} onRefresh={onRefresh} tintColor={colors.violet} /> : undefined}
      >
        {header}
        <View style={s.content}>{children}</View>
      </ScrollView>
    </View>
  );
}

export function Field({ label, style, inputStyle, right, ...props }: TextInputProps & { label?: string; inputStyle?: StyleProp<TextStyle>; right?: React.ReactNode }) {
  return (
    <View style={style as StyleProp<ViewStyle>}>
      {label ? <T size={12} color={colors.muted} style={{ marginBottom: 4 }}>{label}</T> : null}
      <View style={s.fieldBox}>
        <TextInput placeholderTextColor={colors.faint} {...props} style={[s.input, { fontFamily: font.semibold }, Platform.OS === 'web' ? { textAlign: isRTL() ? 'right' : 'left' } : null, inputStyle]} />
        {right}
      </View>
    </View>
  );
}

export function SectionTitle({ children, extra }: { children: React.ReactNode; extra?: string }) {
  return (
    <T w="black" size={16} color={colors.violet} style={{ marginTop: 18, marginBottom: 8 }}>
      {children}
      {extra ? <T w="semibold" size={14} color={colors.faint}>{`  ${extra}`}</T> : null}
    </T>
  );
}

export function Empty({ text, icon = 'file-tray-outline' }: { text: string; icon?: keyof typeof Ionicons.glyphMap }) {
  return (
    <View style={{ alignItems: 'center', paddingVertical: 48, gap: 8 }}>
      <Ionicons name={icon} size={40} color={colors.faint} />
      <T color={colors.muted}>{text}</T>
    </View>
  );
}

export type SelectOption = { key: string; label: string; count?: number; alert?: boolean };

/**
 * Dropdown with several choices: tap to open, tick/untick each option, "select all" / "clear".
 * Nothing ticked = everything (shown as `allLabel`).
 */
export function MultiSelect({ icon, allLabel, many, options, selected, onChange, style }: {
  icon: keyof typeof Ionicons.glyphMap;
  allLabel: string;
  many: (n: number) => string;
  options: SelectOption[];
  selected: string[];
  onChange: (keys: string[]) => void;
  style?: StyleProp<ViewStyle>;
}) {
  const [open, setOpen] = React.useState(false);
  const picked = options.filter((o) => selected.includes(o.key));
  const summary = picked.length === 0 ? allLabel : picked.length === 1 ? picked[0].label : many(picked.length);
  const toggle = (key: string) => onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);

  return (
    <View style={style}>
      <Pressable
        onPress={() => setOpen((v) => !v)}
        style={[s.selectBtn, (open || picked.length > 0) && { borderColor: colors.violet }]}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
      >
        <Ionicons name={icon} size={17} color={colors.violet} />
        <T w="bold" size={13} color={picked.length ? colors.violet : colors.text} style={{ flex: 1 }} numberOfLines={1}>{summary}</T>
        {picked.length ? (
          <View style={s.selectCount}>
            <T w="black" size={11} color={colors.white} style={{ textAlign: 'center' }}>{String(picked.length)}</T>
          </View>
        ) : null}
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={18} color={colors.muted} />
      </Pressable>

      {open ? (
        <View style={s.selectPanel}>
          <View style={[s.between, { paddingHorizontal: 12, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.border }]}>
            <Pressable onPress={() => onChange(options.map((o) => o.key))} hitSlop={8}>
              <T w="bold" size={12} color={colors.violet}>{t('تحديد الكل', 'Select all', 'هەمووی هەڵبژێرە')}</T>
            </Pressable>
            <Pressable onPress={() => onChange([])} hitSlop={8}>
              <T w="bold" size={12} color={colors.danger}>{t('إلغاء التحديد', 'Clear', 'لابردنی هەڵبژاردن')}</T>
            </Pressable>
          </View>
          {options.map((o) => {
            const on = selected.includes(o.key);
            return (
              <Pressable
                key={o.key}
                onPress={() => toggle(o.key)}
                style={({ pressed }) => [s.selectRow, on && { backgroundColor: colors.violetTint }, pressed && { opacity: 0.7 }]}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: on }}
              >
                <Ionicons name={on ? 'checkbox' : 'square-outline'} size={20} color={colors.violet} />
                <T w={on ? 'bold' : 'semibold'} size={13} style={{ flex: 1 }}>{o.label}</T>
                {o.alert ? <Ionicons name="alert-circle" size={15} color={colors.danger} /> : null}
                {o.count !== undefined ? (
                  <View style={[s.selectPill, o.count === 0 && { backgroundColor: colors.greyTint }]}>
                    <T w="bold" size={11} color={o.count ? colors.violetDark : colors.faint} style={{ textAlign: 'center' }}>{String(o.count)}</T>
                  </View>
                ) : null}
              </Pressable>
            );
          })}
          <Pressable onPress={() => setOpen(false)} style={s.selectDone}>
            <T w="bold" size={13} color={colors.white} style={{ textAlign: 'center' }}>{t('تم', 'Done')}</T>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}

/** Dropdown with one choice (same look as MultiSelect). `hint` is shown at the row's end, e.g. a price. */
export function SingleSelect({ icon, placeholder, options, value, onChange, style, invalid }: {
  icon: keyof typeof Ionicons.glyphMap;
  placeholder: string;
  options: (SelectOption & { hint?: string })[];
  value: string | null;
  onChange: (key: string) => void;
  style?: StyleProp<ViewStyle>;
  invalid?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const picked = options.find((o) => o.key === value);

  return (
    <View style={style}>
      <Pressable
        onPress={() => setOpen((v) => !v)}
        style={[s.selectBtn, open || picked ? { borderColor: colors.violet } : invalid ? { borderColor: colors.danger } : null]}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
      >
        <Ionicons name={icon} size={17} color={colors.violet} />
        <T w="bold" size={14} color={picked ? colors.text : colors.faint} style={{ flex: 1 }} numberOfLines={1}>{picked ? picked.label : placeholder}</T>
        {picked?.hint ? <T size={12} color={colors.muted}>{picked.hint}</T> : null}
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={18} color={colors.muted} />
      </Pressable>

      {open ? (
        <View style={[s.selectPanel, { maxHeight: 320 }]}>
          <ScrollView nestedScrollEnabled>
            {options.map((o) => {
              const on = o.key === value;
              return (
                <Pressable
                  key={o.key}
                  onPress={() => {
                    setOpen(false);
                    onChange(o.key);
                  }}
                  style={({ pressed }) => [s.selectRow, on && { backgroundColor: colors.violetTint }, pressed && { opacity: 0.7 }]}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: on }}
                >
                  <Ionicons name={on ? 'radio-button-on' : 'radio-button-off'} size={19} color={colors.violet} />
                  <T w={on ? 'bold' : 'semibold'} size={14} style={{ flex: 1 }}>{o.label}</T>
                  {o.hint ? <T size={12} color={colors.muted}>{o.hint}</T> : null}
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}

/** Shows the current language; tapping it opens the list to pick another (العربية / کوردی / English). */
export function LangSwitch({ onDark }: { onDark?: boolean }) {
  const [open, setOpen] = React.useState(false);
  const current = LANGS.find((l) => l.key === getLang()) ?? LANGS[0];
  const fg = onDark ? colors.white : colors.violet;
  return (
    <View style={{ zIndex: 20, alignSelf: 'flex-start' }}>
      <Pressable
        onPress={() => setOpen((v) => !v)}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        accessibilityLabel={current.label}
        style={({ pressed }) => [s.langBtn, { borderColor: onDark ? 'rgba(255,255,255,0.6)' : colors.violet, opacity: pressed ? 0.7 : 1 }]}
      >
        <Ionicons name="language-outline" size={16} color={fg} />
        <T w="bold" size={13} color={fg}>{current.label}</T>
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={14} color={fg} />
      </Pressable>
      {open ? (
        <View style={s.langPanel}>
          {LANGS.map((l) => {
            const on = l.key === current.key;
            return (
              <Pressable
                key={l.key}
                onPress={() => {
                  setOpen(false);
                  if (!on) setLang(l.key);
                }}
                accessibilityRole="radio"
                accessibilityState={{ checked: on }}
                style={({ pressed }) => [s.langRow, on && { backgroundColor: colors.violetTint }, pressed && { opacity: 0.7 }]}
              >
                <Ionicons name={on ? 'radio-button-on' : 'radio-button-off'} size={17} color={colors.violet} />
                <T w={on ? 'bold' : 'semibold'} size={14}>{l.label}</T>
              </Pressable>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

export function Loading() {
  return (
    <View style={{ paddingVertical: 48, alignItems: 'center' }}>
      <ActivityIndicator color={colors.violet} size="large" />
    </View>
  );
}

export function ErrorNote({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <View style={s.error}>
      <Ionicons name="alert-circle" size={18} color={colors.danger} />
      <T size={13} color={colors.danger} style={{ flex: 1 }}>{text}</T>
    </View>
  );
}

export const s = StyleSheet.create({
  card: {
    backgroundColor: colors.card,
    borderRadius: radius.lg,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#2A1240',
    shadowOpacity: 0.06,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 3 },
    elevation: 1,
  },
  selectBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: colors.white, borderWidth: 1.5, borderColor: colors.border, borderRadius: radius.md, paddingHorizontal: 12, paddingVertical: 10 },
  selectCount: { minWidth: 20, height: 20, borderRadius: 10, backgroundColor: colors.violet, justifyContent: 'center', paddingHorizontal: 5 },
  selectPanel: { marginTop: 6, backgroundColor: colors.white, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border, overflow: 'hidden', shadowColor: '#2A1240', shadowOpacity: 0.12, shadowRadius: 14, shadowOffset: { width: 0, height: 6 }, elevation: 4 },
  selectRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F4F1F8' },
  selectPill: { minWidth: 26, borderRadius: 12, backgroundColor: colors.violetTint, paddingHorizontal: 6, paddingVertical: 1 },
  selectDone: { margin: 10, borderRadius: radius.sm, backgroundColor: colors.violet, paddingVertical: 9 },
  langBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1.5, borderRadius: 20, paddingHorizontal: 12, paddingVertical: 5, alignSelf: 'flex-start' },
  langPanel: {
    // Same edge as the button (it sits at the screen's edge), opening inwards.
    position: 'absolute', top: 38, end: 0, minWidth: 150, backgroundColor: colors.white, borderRadius: radius.md, paddingVertical: 4,
    shadowColor: '#2A1240', shadowOpacity: 0.18, shadowRadius: 14, shadowOffset: { width: 0, height: 6 }, elevation: 6,
  },
  langRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, paddingVertical: 9 },
  pill: { paddingHorizontal: 10, paddingVertical: 2, borderRadius: 20, alignSelf: 'flex-start' },
  btn: { borderRadius: radius.md, paddingVertical: 12, paddingHorizontal: 16, alignItems: 'center', justifyContent: 'center' },
  btnRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 20 },
  header: { paddingHorizontal: 18, paddingBottom: 18, borderBottomLeftRadius: 24, borderBottomRightRadius: 24 },
  content: { paddingHorizontal: 14, paddingTop: 6, width: '100%', maxWidth: 760, alignSelf: 'center' },
  fieldBox: { flexDirection: 'row', alignItems: 'center', borderWidth: 1.5, borderColor: '#E4DFEC', borderRadius: radius.md, backgroundColor: colors.white, paddingHorizontal: 12 },
  // font set per render (language); minWidth 0: a wide font must not push the icon out of the box
  input: { flex: 1, minWidth: 0, paddingVertical: 10, fontSize: 15, color: colors.text },
  row: { flexDirection: 'row', alignItems: 'center' },
  between: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  error: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: colors.dangerTint, borderRadius: radius.md, padding: 10, marginVertical: 8 },
});
